import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Fade,
  Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { FormikProps } from "formik";
import { TripFormValues, Faq } from "../types/tripTypes";
import RichTextEditor from "components/shared/TextEditor/RTE";

interface TripFaqsProps {
  formik: FormikProps<TripFormValues>;
  editable?: boolean;
}

const TripFaqs: React.FC<TripFaqsProps> = ({ formik, editable = true }) => {
  const { values, setFieldValue } = formik;
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const handleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleChange = (index: number, field: keyof Faq, value: string) => {
    const updatedFaqs = [...values.faq];
    updatedFaqs[index] = { ...updatedFaqs[index], [field]: value };
    setFieldValue("faq", updatedFaqs);
  };

  const addFaq = () => {
    const newFaq: Faq = {
      id: values.faq.length + 1,
      question: "",
      answer: "",
    };
    setFieldValue("faq", [...values.faq, newFaq]);
    setExpandedIndex(values.faq.length); 
  };

  const removeFaq = (index: number) => {
    const updatedFaqs = values.faq.filter((_, i) => i !== index);
    setFieldValue("faq", updatedFaqs);

    // Adjust expanded index if necessary
    if (expandedIndex === index) {
      setExpandedIndex(null);
    } else if (expandedIndex !== null && expandedIndex > index) {
      setExpandedIndex(expandedIndex - 1);
    }
  };

  const getCharacterCount = (text: string = "", maxLength: number) => {
    const remaining = maxLength - text.length;
    const color =
      remaining < 20 ? "#f44336" : remaining < 50 ? "#ff9800" : "#535862";
    return { remaining, color };
  };

  return (
    <Card
      sx={{
        marginTop: 3,
        padding: 2,
        width: "100%",
        marginX: "auto",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        borderRadius: 2,
        border: "1px solid #E8EAED",
      }}
    >
      <CardContent sx={{ padding: 3 }}>
        <Box sx={{ marginBottom: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              marginBottom: 0.5,
            }}
          >
            <Typography
              fontWeight={600}
              variant="h6"
              color="#181D27"
              sx={{ fontSize: "20px" }}
            >
              FAQs
            </Typography>
            <Tooltip
              title="Add frequently asked questions to help travelers get quick answers"
              arrow
            >
              <HelpOutlineIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
            </Tooltip>
          </Box>
          <Typography
            variant="body2"
            color="#535862"
            sx={{ fontSize: "14px", lineHeight: 1.5 }}
          >
            Help travelers by answering common questions about your trip.
          </Typography>
        </Box>

        {values.faq.length === 0 ? (
          <Box
            sx={{
              padding: 4,
              textAlign: "center",
              backgroundColor: "#FAFBFC",
              borderRadius: 2,
              border: "1px solid #E8EAED",
              marginBottom: 3,
            }}
          >
            <HelpOutlineIcon
              sx={{ fontSize: 48, color: "#D1D5DB", marginBottom: 2 }}
            />
            <Typography color="#6B7280" sx={{ fontSize: "14px" }}>
              No FAQs added yet. Start by adding your first FAQ to help
              travelers.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {values.faq.map((faq, index) => {
              const isExpanded = expandedIndex === index;
              const questionCount = getCharacterCount(faq.question, 200);
              const answerCount = getCharacterCount(faq.answer, 500);

              return (
                <Accordion
                  key={faq.id}
                  expanded={isExpanded}
                  onChange={() => handleExpand(index)}
                  sx={{
                    "&:before": { display: "none" },
                    border: "1px solid #E0E3E7",
                    borderRadius: "12px !important",
                    boxShadow: isExpanded
                      ? "0 4px 12px rgba(0,0,0,0.1)"
                      : "0 1px 3px rgba(0,0,0,0.05)",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      borderColor: "#C1C7CD",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon
                        sx={{
                          color: "#535862",
                          transition: "transform 0.2s ease-in-out",
                        }}
                      />
                    }
                    sx={{
                      margin: 0,
                      padding: "8px 20px",
                      minHeight: "60px",
                      "&.Mui-expanded": {
                        minHeight: "60px",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                        gap: 2,
                      }}
                    >
                      <Typography
                        variant="body1"
                        color="#181D27"
                        fontWeight={600}
                        sx={{
                          flexShrink: 0,
                          fontSize: "16px",
                        }}
                      >
                        FAQ {index + 1}
                      </Typography>

                      {/* Show Question Preview When Collapsed */}
                      {faq.question && !isExpanded && (
                        <Fade in={!isExpanded} timeout={200}>
                          <Typography
                            variant="body2"
                            color="#6B7280"
                            sx={{
                              fontSize: "14px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {faq.question}
                          </Typography>
                        </Fade>
                      )}

                      {/* Delete Button */}
                      <Tooltip title="Remove this FAQ" arrow>
                        <IconButton
                          aria-label="delete"
                          size="small"
                          disabled={!editable}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFaq(index);
                          }}
                          sx={{
                            marginLeft: "auto",
                            color: editable ? "#6B7280" : "#D1D5DB",
                            "&:hover": {
                              color: editable ? "#DC2626" : "#D1D5DB",
                              backgroundColor: editable
                                ? "rgba(220, 38, 38, 0.1)"
                                : "transparent",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ padding: "0 20px 20px 20px" }}>
                    <Box sx={{ borderTop: "1px solid #F3F4F6", paddingTop: 3 }}>
                      {/* Question Input */}
                      <Box sx={{ marginBottom: 3 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={500}
                          sx={{
                            marginBottom: 1.5,
                            color: "#374151",
                            fontSize: "14px",
                            fontWeight: "bold",
                          }}
                        >
                          Question *
                        </Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          value={faq.question}
                          disabled={!editable}
                          onChange={(e) =>
                            handleChange(index, "question", e.target.value)
                          }
                          inputProps={{ maxLength: 200 }}
                          placeholder="What would travelers commonly ask about this trip?"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                              fontSize: "14px",
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#9CA3AF",
                              },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#3B82F6",
                                },
                            },
                          }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: 1,
                          }}
                        >
                          <Typography
                            sx={{ fontSize: "12px", color: "#9CA3AF" }}
                          >
                            Keep it clear and specific
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: questionCount.color,
                              fontWeight:
                                questionCount.remaining < 20 ? 600 : 400,
                            }}
                          >
                            {questionCount.remaining} characters left
                          </Typography>
                        </Box>
                      </Box>

                      {/* Answer Input */}
                      <Box>
                        <Typography
                          variant="subtitle2"
                          fontWeight={500}
                          sx={{
                            marginBottom: 1.5,
                            color: "#374151",
                            fontSize: "14px",
                            fontWeight: "bold",
                          }}
                        >
                          Answer *
                        </Typography>
                        <RichTextEditor
                          value={faq?.answer || ""}
                          onChange={(value) =>
                            handleChange(index, "answer", value)
                          }
                          placeholder={"Provide a helpful, detailed answer"}
                          disabled={!editable}
                          maxLength={400}
                          minHeight={140}
                        />
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )}

        {/* Add New FAQ Button */}
        {editable && (
          <Fade in={true} timeout={300}>
            <Box
              onClick={addFaq}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
                color: "#374151",
                fontWeight: 500,
                marginTop: 3,
                border: "2px dashed #D1D5DB",
                borderRadius: "12px",
                padding: "16px 24px",
                minHeight: "56px",
                fontFamily: "Inter, sans-serif",
                cursor: "pointer",
                transition: "all 0.2s ease-in-out",
                backgroundColor: "#FAFBFC",
                "&:hover": {
                  backgroundColor: "#F3F4F6",
                  borderColor: "#9CA3AF",
                  color: "#1F2937",
                },
                "&:active": {
                  transform: "translateY(1px)",
                },
              }}
            >
              <AddIcon sx={{ fontSize: "20px" }} />
              <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                Add New FAQ
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Helper Text */}
        {values.faq.length > 0 && (
          <Box
            sx={{
              marginTop: 3,
              padding: 2,
              backgroundColor: "#F0F9FF",
              border: "1px solid #BAE6FD",
              borderRadius: "8px",
            }}
          >
            <Typography
              sx={{
                fontSize: "13px",
                color: "#0369A1",
                textAlign: "center",
              }}
            >
              💡 <strong>Tip:</strong> Good FAQs address booking, cancellation,
              what to bring, difficulty level, and group size questions.
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TripFaqs;
